import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Save, MapPin, Phone, Mail, Clock, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactManagementPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    page_title: '',
    page_subtitle: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    alternate_email: '',
    office_hours: '',
    google_maps_embed_url: '',
    form_title: '',
    form_recipient_email: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/contact-settings`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching contact settings:', error);
      toast.error('Failed to load contact settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate required fields
      if (!formData.email || !formData.form_recipient_email) {
        toast.error('Email and Form Recipient Email are required');
        setSaving(false);
        return;
      }

      await axios.post(
        `${API_URL}/api/contact-settings`,
        formData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Contact settings saved successfully!');
      await fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save contact settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#0E6D2B]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-8 h-8 text-[#0E6D2B]" />
              Contact Page Management
            </h1>
            <p className="text-gray-600 mt-1">Manage contact page content, information, and Google Maps</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        <div className="space-y-6">
          {/* Page Header Section */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#0E6D2B]" />
              Page Header
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Page Title</Label>
                <Input
                  value={formData.page_title}
                  onChange={(e) => updateField('page_title', e.target.value)}
                  placeholder="Contact Us"
                />
              </div>
              <div>
                <Label>Page Subtitle</Label>
                <Input
                  value={formData.page_subtitle}
                  onChange={(e) => updateField('page_subtitle', e.target.value)}
                  placeholder="Get in touch with ICAR-CISH"
                />
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0E6D2B]" />
              Contact Information
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Address *</Label>
                <Textarea
                  rows={4}
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="Institute Address (use \n for new lines)"
                />
                <p className="text-xs text-gray-500 mt-1">Use separate lines for street, city, state, country</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+91-522-2841022, 2841023"
                  />
                </div>
                <div>
                  <Label>Fax (Optional)</Label>
                  <Input
                    value={formData.fax || ''}
                    onChange={(e) => updateField('fax', e.target.value)}
                    placeholder="+91-522-2841025"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Primary Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="director.cish@icar.gov.in"
                  />
                </div>
                <div>
                  <Label>Alternate Email (Optional)</Label>
                  <Input
                    type="email"
                    value={formData.alternate_email || ''}
                    onChange={(e) => updateField('alternate_email', e.target.value)}
                    placeholder="cishlko@gmail.com"
                  />
                </div>
              </div>

              <div>
                <Label>Office Hours</Label>
                <Textarea
                  rows={3}
                  value={formData.office_hours}
                  onChange={(e) => updateField('office_hours', e.target.value)}
                  placeholder="Monday - Friday: 9:00 AM - 5:30 PM\nSaturday: 9:00 AM - 1:00 PM"
                />
                <p className="text-xs text-gray-500 mt-1">Use separate lines for different days</p>
              </div>
            </div>
          </div>

          {/* Google Maps Section */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0E6D2B]" />
              Google Maps Integration
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Google Maps Embed URL</Label>
                <Textarea
                  rows={3}
                  value={formData.google_maps_embed_url}
                  onChange={(e) => updateField('google_maps_embed_url', e.target.value)}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  className="font-mono text-xs"
                />
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs text-blue-800 font-medium mb-1">📍 How to get Google Maps Embed URL:</p>
                  <ol className="text-xs text-blue-700 space-y-1 ml-4 list-decimal">
                    <li>Go to <a href="https://www.google.com/maps" target="_blank" className="underline">Google Maps</a></li>
                    <li>Search for your institute location</li>
                    <li>Click "Share" button</li>
                    <li>Click "Embed a map" tab</li>
                    <li>Copy the URL from the iframe src attribute</li>
                    <li>Paste it here</li>
                  </ol>
                </div>
              </div>
              
              {/* Map Preview */}
              {formData.google_maps_embed_url && (
                <div>
                  <Label>Map Preview</Label>
                  <div className="rounded-lg overflow-hidden h-64 bg-gray-100">
                    <iframe
                      src={formData.google_maps_embed_url}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      title="Location Preview"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Form Settings */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#0E6D2B]" />
              Contact Form Settings
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Form Title</Label>
                <Input
                  value={formData.form_title}
                  onChange={(e) => updateField('form_title', e.target.value)}
                  placeholder="Send us a Message"
                />
              </div>
              <div>
                <Label>Form Recipient Email *</Label>
                <Input
                  type="email"
                  value={formData.form_recipient_email}
                  onChange={(e) => updateField('form_recipient_email', e.target.value)}
                  placeholder="director.cish@icar.gov.in"
                />
                <p className="text-xs text-gray-500 mt-1">
                  📧 This email will receive messages when users click "Send Message" (via mailto)
                </p>
              </div>
            </div>
          </div>

          {/* Save Button at Bottom */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
